// The account station: where a quote the lead form made becomes an
// account. The grant it asks for lets the sign-in screen confirm the phone
// (or email) typed at submission, so nothing here accepts or edits a
// destination; the sign-in lands back on this station, signed in.

import { useEffect, useState, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { ShieldCheckIcon } from "@heroicons/react/24/outline";
import { Link, useNavigate } from "react-router-dom";

import ArcLoading from "components/ArcLoading";
import Button from "components/Button";
import { LINK_BUTTON_CLASS } from "components/FlowButtons";
import { CONTINUE_BUTTON_CLASS } from "components/FormV2";
import NoticeBanner from "components/NoticeBanner";
import ScreenChrome from "components/ScreenChrome";
import { SUPPORT_PHONE_HREF, SUPPORT_PHONE_LABEL } from "constants/phones";
import { useUser } from "context/UserContext";
import Skeleton from "components/Skeleton";

import { finishedPolicyDestination, signInPathForQuote } from "./account";
import { startAccountGrant } from "./account-client";
import { useQuoteFlowAnalytics } from "./analytics";
import { fetchFinalizeView } from "./api";

type Outcome =
  | { kind: "idle" }
  | { kind: "requesting" }
  | { kind: "not_available" }
  | { kind: "existing_account" }
  | { kind: "error" };

function SupportLine() {
  return (
    <p className="text-sm text-oat-700">
      Questions? Call us at{" "}
      <a href={SUPPORT_PHONE_HREF} className="font-bold text-soil underline">
        {SUPPORT_PHONE_LABEL}
      </a>
      . We&rsquo;re here 24/7.
    </p>
  );
}

/** The quote's policy id once the finalize view answers; `undefined` while
 * it is unread, null when it names none or cannot be read. */
function usePolicyId(quoteId: string): string | null | undefined {
  const [policyId, setPolicyId] = useState<string | null | undefined>(
    undefined,
  );
  useEffect(() => {
    let cancelled = false;
    fetchFinalizeView(quoteId)
      .then((view) => {
        if (!cancelled) setPolicyId(view.policy_id ?? null);
      })
      .catch(() => {
        if (!cancelled) setPolicyId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [quoteId]);
  return policyId;
}

function SignedInFace({ quoteId }: { quoteId: string }) {
  const policyId = usePolicyId(quoteId);
  const destination = finishedPolicyDestination(policyId);
  return (
    <ScreenChrome
      kicker="Your account"
      titleA="You're signed in."
      titleB="Your quote is saved."
      blurb="Your quote, and everything that comes after it, lives in your account."
    >
      <div className="flex flex-col items-start gap-5">
        {policyId === undefined ? (
          <Skeleton className="h-12 w-48 rounded-md" />
        ) : (
          <Link to={destination.to} className={CONTINUE_BUTTON_CLASS}>
            {destination.label}
          </Link>
        )}
        <SupportLine />
      </div>
    </ScreenChrome>
  );
}

function outcomeNotice(outcome: Outcome): ReactNode {
  if (outcome.kind === "existing_account") {
    return (
      <NoticeBanner>
        There&rsquo;s already an account for this quote. Sign in to open it.
      </NoticeBanner>
    );
  }
  if (outcome.kind === "not_available") {
    return (
      <NoticeBanner>
        We can&rsquo;t set up an account from this link. If you already have
        one, sign in. Otherwise call us and we&rsquo;ll help.
      </NoticeBanner>
    );
  }
  if (outcome.kind === "error") {
    return (
      <NoticeBanner tone="error">
        Something went wrong. Please try again, or call us.
      </NoticeBanner>
    );
  }
  return null;
}

export default function AccountStep({ quoteId }: { quoteId: string }) {
  const { user, loading } = useUser();
  const navigate = useNavigate();
  const { track } = useQuoteFlowAnalytics();
  const [outcome, setOutcome] = useState<Outcome>({ kind: "idle" });

  if (loading) {
    return (
      <main className="flex flex-1 items-center justify-center px-4 py-24">
        <ArcLoading className="h-8 w-[130px] text-soil" />
      </main>
    );
  }

  if (user) return <SignedInFace quoteId={quoteId} />;

  const begin = async () => {
    setOutcome({ kind: "requesting" });
    const answer = await startAccountGrant(quoteId, (error) =>
      Sentry.captureException(error),
    );
    if (answer.kind === "granted") {
      track("quote_flow_account_started", {});
      navigate("/login", { state: answer.state });
      return;
    }
    setOutcome(
      answer.kind === "rate_limited" || answer.kind === "failed"
        ? { kind: "error" }
        : { kind: answer.kind },
    );
  };

  const unable =
    outcome.kind === "existing_account" || outcome.kind === "not_available";

  return (
    <ScreenChrome
      kicker="Your account"
      titleA="Create your"
      titleB="account."
      blurb="Save your quote so you can come back to it from any device."
    >
      <div className="flex flex-col items-start gap-5">
        {outcomeNotice(outcome)}
        <div className="flex items-start gap-3.5 rounded-xl bg-oat px-5 py-4">
          <ShieldCheckIcon
            className="mt-0.5 h-5 w-5 flex-none text-evergreen"
            strokeWidth={2}
            aria-hidden="true"
          />
          <p className="text-base leading-relaxed text-oat-800">
            Your policy, signed documents, and payment history live in your
            account. It&rsquo;s the only place your application details are
            visible, which is why we confirm it&rsquo;s you.
          </p>
        </div>
        {unable ? (
          <Link
            to={signInPathForQuote(quoteId)}
            className={CONTINUE_BUTTON_CLASS}
          >
            Sign in
          </Link>
        ) : (
          <Button
            variant="continue"
            onClick={begin}
            isLoading={outcome.kind === "requesting"}
            loadingText="One moment..."
          >
            Confirm it&apos;s me
          </Button>
        )}
        {!unable && (
          <Link to={`/quotes/${quoteId}`} className={LINK_BUTTON_CLASS}>
            Back to my quote
          </Link>
        )}
        <SupportLine />
      </div>
    </ScreenChrome>
  );
}
